import { mapState as rootMapState, mapActions as rootMapActions } from 'pinia';
import { useUserStore, usePageStore } from '@/store';
import register from '@/mixins/register';
import mapMixin from './map.mixin';
import questionnaire from '@/components/questionnaire';
import { unformats } from '@/common/format';
import { getFromDetailPageParam } from '@/common/business/agent-detail';
import { openLoginHideOtherPop } from '@/components/sign-log';
import { defineAsyncComponent } from 'vue';
import util from '@/common/util';
import Disclaimer from '@/components/disclaimer';
import { INTERCEPT_URL } from '@/common/customize';
import { adNewPhoneValid } from '@/components/registerPhoneValidPop/index.js';
import thirdPart from '@/components/sign-log/third-part.js';
import { getPhysicalInfo } from '@/common/getPhysicalInfo.js'

export default {
    emits: ['loaded'],
    i18next: {
        ns: 'md-evaluation-banner'
    },
    mixins: [register, thirdPart, mapMixin],
    components: {
        registerMap: defineAsyncComponent(() =>
            import(
                /* webpackChunkName: "map_classic"*/ '@/components/map/classic.vue'
            )),
        registerHereMap: defineAsyncComponent(() =>
            import(
                /* webpackChunkName: "map-here_classic"*/ '@/components/map-here/classic.vue'
            )),
        Disclaimer
    },
    data: function() {
        return {
            gmailDisabled: false,
            autoJump: true, //  Whether to automatically jump
            emailValidate: {
                async: '/api-site/register/hasUser?account={value}',
                'async-callback': this.hasUserCallBack
            },
            loadMap: false,
            disclaimerChecked: false,
            disclaimerUniqId: util.uuidv4(),
            showNewRegister: false
        };
    },
    computed: {
        ...rootMapState(usePageStore, {
            page: store => store.page || {}
        }),
        ...rootMapState(useUserStore, {
            user: store => store.user
        }),
        registerMapType({ page }) {
            return page.map?.type === 'google'
                ? 'registerMap'
                : 'registerHereMap';
        },
        role() {
            return '1';
        },
        inquiry() {
            //  extract from global  query  configuration
            if (this.theme === 'tips' || this.theme === 'seller' || this.isRegisterAgentLead) {
                return false;
            } else {
                return this.page
                    ? this.page.questionNaire.inquirySeller
                    : false;
            }
        },
        disclaimerSubmitDisabled() {
            return !this.disclaimerChecked || this.submitDisabled;
        },

    },
    watch: {
        // TODO:  Whether the phone is required affects this logic
        //  Detect third-party authorization and log in to jump directly
        user(newVal) {
            //  Automatic jump is true to trigger the jump
            if (newVal && this.thirdParty && this.autoJump) {
                this.registerNext();
            }
        }
    },
    props: {
        nameRequired: {
            //  Name is required
            type: Boolean,
            default: false
        },
        namePlaceholder: String,
        extrahtml: String,
        phoneRequired: {
            //  Phone is required
            type: Boolean,
            default: true
        },
        address: {
            type: String,
            default: ''
        },
        unit: {
            type: String
        },
        title: {
            type: String
        },
        nameType: {
            type: [Number, String]
        },
        subTitle: {
            type: String,
            default: ''
        },
        submitText: {
            type: String
        },
        buttonStyle: {
            type: Object
        },
        options: {
            type: Object,
            default: () => ({})
        },
        theme: {
            type: String,
            default: ''
        }
    },
    methods: {
        ...rootMapActions(useUserStore, ['updateUser', 'login']),
        ...rootMapActions(usePageStore, ['setCurrentUniqId', 'getCustomParams']),
        registerNext() {
            //  Enable registration button
            this.submitDisabled = false;
            try {
                //  trigger jump
                if (!this.isRegisterAgentLead) {
                    this.onRegisterCallback(this.options.onregister, false);
                }
            } catch (e) {
                console.log(e)
            }
            
            if (this.showNewRegister) {
                adNewPhoneValid.openRegisterPhoneValidPop({
                    leadType: 'Seller'
                });
                return
            }
            //  Controlling Questionnaire Popups
            this.inquiry &&
                this.showQuestionnaire(
                    Object.assign({}, this.options.questionnaire || {}, {
                        noToast: true,
                        closeCallback: () => {
                            this.onSuccessCallback();
                        }
                    })
                );
        },
        registerSubmitSuccess(data, isAutoRegister) {
            if (this.disclaimerSubmitDisabled && !isAutoRegister) {
                return; 
            }
            !isAutoRegister && (this.submitDisabled = true);
            //  enable jump
            this.autoJump = true;
            data = { ...data, ...adNewPhoneValid.addAdNewRegisterParams() };
            this.formatData(
                Object.assign(data, {
                    address: this.address,
                    unit: this.unit,
                    pageUrl: this.pageLocation.href
                })
            );
            if (this.thirdParty !== '') {
                //  Trigger third-party login
                let postData = Object.assign({}, this.authData, data);
                if (this.user) {
                    //  If the user is logged in, only update the phone and trigger the jump
                    this.updatePhoneNum(data);
                } else {
                    //  The user does not log in and directly triggers third-party registration
                    this.thridPartFormatDate(postData);
                    isAutoRegister
                        ? this.thirdPartyAutoRegister(postData)
                        : this.thirdPartyRegister(postData);
                }
            } else {
                this.setCurrentUniqId(
                    INTERCEPT_URL.leadRegister,
                    this.disclaimerUniqId
                );
                //  Trigger normal login and jump directly
                isAutoRegister
                    ? this.submitRegisterInfo(null, true)
                    : this.leadRegister();
            }
        },
        leadAutoRegister(data) {
            this.registerSubmitSuccess(data, true);
        },
        async updatePhoneNum(data) {
            //  If the phone number is empty, jump directly
            if (!data.phone) {
                this.registerNext();
            } else {
                const phoneNumber = unformats.number(data.phone);
                // TCPA consent
                let customParams = this.getCustomParams(INTERCEPT_URL.updatePhone);
                let physicalInfo = await getPhysicalInfo();
                customParams.physicalInfo = physicalInfo;
                const params = {
                    phoneNumber,
                    ...customParams
                }
                this.axiosIns
                    .post('/api-site/profile/update/phoneNumber', params)
                    .then(() => {
                        this.updateUser({ phoneNumber });
                        this.registerNext();
                    })
                    .catch(error => {
                        this.registerNext();
                        console.log(error);
                    });
            }
        },
        thirdPartyRegisterNext(data) {
            this.thridPartFormatDate(data);
            this.thirdPartyRegister(data);
        },
        thirdShowQuestionnaire() {
            //  Jump after the third login is successful
            this.registerNext();
        },
        //  Trigger third-party login after authorization
        thirdPartySignLog() {
            this.axiosIns
                .post('/api-site/third-party/login', this.authData)
                .then(res => {
                    const resData = res.data;
                    const user = resData.user;
                    this.$EventBus.$emit('siteTrack', {
                        ...this.authData,
                        user: user,
                        trackType: 'ThirdPartyLogin',
                        ele: this.$el
                    });
                    //  User Existence Description Registered Direct Jump
                    if (user) {
                        if (resData.leadAccid && resData.leadToken) {
                            user.leadAccid = resData.leadAccid;
                            user.leadToken = resData.leadToken;
                        }
                        this.login({ user });
                    } else {
                        //  The user does not exist to go through the registration logic
                        const postData = Object.assign(
                            this.authData,
                            {
                                account: this.authData.email,
                                role: this.role
                            },
                            this.registerData || {}
                        );
                        postData.canAssignToLender = true;
                        this.authData = postData;
                        //  Telephone is not required and there is an email ， automatic registration
                        if (
                            postData.account &&
                            (this.isFbLead || !this.phoneRequired)
                        ) {
                            //  Prevent automatic jump after detecting user login
                            this.autoJump = false;
                            this.thirdPartyRegisterNext(postData);
                        } else {
                            //  The phone number is required Wait for the user to enter the phone number and trigger the registration manually
                        }
                    }
                })
                .catch(error => {
                    this.$EventBus.$emit('siteTrack', {
                        ...this.authData,
                        errorData: error,
                        trackType: 'ThirdPartyLogin-fail',
                        ele: this.$el
                    });
                    this.thirdPartyLoginError(error);
                });
        },
        thirdPartyRegister(data) {
            //  no data  or email register
            if (!data || !data.account) {
                return; 
            }
            this.axiosIns
                .post('/api-site/third-party/register', getFromDetailPageParam(data))
                .then(res => {
                    const resData = res.data;
                    const user = resData.user;
                    if (resData.leadAccid && resData.leadToken) {
                        user.leadAccid = resData.leadAccid;
                        user.leadToken = resData.leadToken;
                    }
                    this.inquiry && questionnaire.prepare(undefined, this.page);
                    this.$EventBus.$emit('siteTrack', {
                        ...data,
                        trackType: 'register',
                        id: user?.id,
                        teamId: user?.teamId,
                        ele: this.$el
                    });
                    if (this.autoJump) {
                        //  Trigger jump if jump is allowed
                        this.login({ user });
                        this.registerNext();
                    } else {
                        //  Avoid jumping directly after the user logs in
                        user.disableJump = true;
                        //  Only if phone is optional
                        this.login({ user });
                        if (!this.phoneRequired) {
                            this.registerSubmitSuccess();
                        }
                    }
                })
                .catch(error => {
                    this.$EventBus.$emit('siteTrack', {
                        from: this.authData.from,
                        errorData: error,
                        trackType: 'lead-register-fail',
                        ele: this.$el
                    });
                    this.thirdPartyRegisterError(error);
                });
        },
        hasUserCallBack(flag, error, value) {
            if (
                !flag && error?.code === 210003
            ) {
                setTimeout(() => {
                    openLoginHideOtherPop(this.options, value);
                }, 1000);
            }
        },
        resizeCallback() {
            if (this.psMap) {
                this.psMap.then(() => {
                    if (this.siteMap && this.siteMap.mapResize) {
                        this.siteMap.mapResize();
                    }
                });
            }
        },
    },
    mounted() {
        this.$nextTick(() => {
            this.loadMap = true;
        });
        this.$emit('loaded');
        this.showNewRegister = adNewPhoneValid.showNewRegister();
    },
    unmounted() {
        window.removeEventListener('thirdLoaded', this.refreshMapSize);
    }
};
